import React, { useState } from 'react';
import { Menu as MenuIcon, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const Header = ({ onOpenGallery, onOpenAffiliate, onOpenCollaborators }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const navLinks = [
    { label: t('nav.inicio'), section: 'hero' },
    { label: t('nav.menu'), section: 'menu' },
    { label: t('nav.nosotros'), section: 'about' },
    { label: t('nav.ubicacion'), section: 'location' }
  ];

  const modalLinks = [
    { label: t('nav.galeria'), action: onOpenGallery },
    { label: t('nav.afiliados'), action: onOpenAffiliate },
    { label: t('nav.colaboradores'), action: onOpenCollaborators }
  ];

  const linkClass =
    'font-body uppercase text-[13px] text-[#C2C1B6] hover:text-[#C9A84C] transition-colors duration-200';

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: 'rgba(10,10,10,0.85)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(201,168,76,0.18)'
        }}
        data-testid="site-header"
      >
        <nav className="mx-auto max-w-[1400px] px-6 lg:px-[60px] py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => scrollToSection('hero')}
              className="flex items-center gap-3"
              data-testid="logo-home-button"
            >
              <span
                className="flex items-center justify-center"
                style={{ width: '38px', height: '38px', background: '#C8282B', borderRadius: '8px' }}
              >
                <span className="text-white font-bold" style={{ fontSize: '20px' }}>山</span>
              </span>
              <span className="font-display text-white" style={{ fontSize: '22px', fontWeight: 700 }}>
                Yamauchi
              </span>
            </button>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center" style={{ gap: '34px', letterSpacing: '1.5px' }}>
              {navLinks.map((item) => (
                <button
                  key={item.section}
                  onClick={() => scrollToSection(item.section)}
                  className={linkClass}
                  style={{ letterSpacing: '1.5px', fontWeight: 500 }}
                  data-testid={`nav-${item.section}`}
                >
                  {item.label}
                </button>
              ))}
              {modalLinks.map((item) => (
                <button
                  key={item.label}
                  onClick={item.action}
                  className={linkClass}
                  style={{ letterSpacing: '1.5px', fontWeight: 500 }}
                  data-testid={`nav-modal-${item.label}`}
                >
                  {item.label}
                </button>
              ))}

              <button
                onClick={() => navigate('/pedir')}
                className="font-body uppercase text-[12px] px-4 py-1.5 transition-colors duration-200"
                style={{ background: '#C8282B', color: '#fff', letterSpacing: '1.5px', fontWeight: 600 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#9B1F22')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#C8282B')}
                data-testid="nav-pedir"
              >
                Pedir
              </button>

              <button
                onClick={toggleLanguage}
                className="font-body text-[12px] px-3 py-1.5 border text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#0A0A0A] transition-colors duration-200"
                style={{ borderColor: 'rgba(201,168,76,0.45)', letterSpacing: '1px' }}
                data-testid="language-toggle-desktop"
              >
                {language === 'es' ? 'ES / EN' : 'EN / ES'}
              </button>
            </div>

            {/* Mobile controls */}
            <div className="flex items-center gap-3 lg:hidden">
              <button
                onClick={toggleLanguage}
                className="font-body text-[11px] px-2 py-1 border text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#0A0A0A] transition-colors duration-200"
                style={{ borderColor: 'rgba(201,168,76,0.45)' }}
                data-testid="language-toggle-mobile"
              >
                {language === 'es' ? 'ES/EN' : 'EN/ES'}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-[#F5F5F0] p-2"
                data-testid="mobile-menu-toggle"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        data-testid="mobile-menu-overlay"
      >
        <div className="absolute inset-0" style={{ background: 'rgba(10,10,10,0.97)', backdropFilter: 'blur(14px)' }} />
        <div className="relative min-h-screen flex items-center justify-center">
          <nav className="text-center flex flex-col" style={{ gap: '28px' }}>
            <button
              onClick={() => { navigate('/pedir'); setIsMenuOpen(false); }}
              className="font-display text-2xl transition-colors duration-200"
              style={{ color: '#C8282B' }}
              data-testid="mobile-nav-pedir"
            >
              Pedir Delivery
            </button>
            {navLinks.map((item) => (
              <button
                key={item.section}
                onClick={() => scrollToSection(item.section)}
                className="font-display text-2xl text-white hover:text-[#C9A84C] transition-colors duration-200"
              >
                {item.label}
              </button>
            ))}
            {modalLinks.map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  item.action();
                  setIsMenuOpen(false);
                }}
                className="font-display text-2xl text-white hover:text-[#C9A84C] transition-colors duration-200"
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
